import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { MemoPlanAccordion } from 'src/pages/Settings/memberships/components/plans-tab/plan-accordion';

interface SortablePlanItemProps {
  planId: string;
  index: number;
  expanded: boolean;
  onExpandedChange: (planId: string, expanded: boolean) => void;
  readOnly: boolean;
  reorderDisabled: boolean;
}

const getDragCursor = (canDrag: boolean, isDragging: boolean): string => {
  if (!canDrag) {
    return 'default';
  }
  return isDragging ? 'grabbing' : 'grab';
};

export const SortablePlanItem = ({
  planId,
  index,
  expanded,
  onExpandedChange,
  readOnly,
  reorderDisabled,
}: SortablePlanItemProps): React.JSX.Element => {
  const canDrag = !reorderDisabled;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    {
      id: planId,
      disabled: !canDrag,
    },
  );

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
        backgroundColor: isDragging ? 'background.paper' : undefined,
        // the drag handle only surfaces on hover, matching the other reorderable lists
        '& .plan-drag-handle': { opacity: isDragging ? 1 : 0, transition: 'opacity 120ms' },
        '&:hover .plan-drag-handle': { opacity: canDrag ? 1 : 0 },
      }}
    >
      <Box
        className="plan-drag-handle"
        {...attributes}
        {...listeners}
        aria-label="Reorder plan"
        sx={{
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'stretch',
          cursor: getDragCursor(canDrag, isDragging),
          px: 0.25,
        }}
      >
        <DynamicIcon
          name="drag_indicator"
          sx={{ color: 'text.secondary', fontSize: '1.125rem' }}
        />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <MemoPlanAccordion
          index={index}
          planId={planId}
          expanded={expanded}
          onExpandedChange={onExpandedChange}
          readOnly={readOnly}
        />
      </Box>
    </Box>
  );
};
